import axios from '../utils/axiosConfig'; 
import { API_BASE_URL, WS_BASE_URL, IS_LOCAL_DEV } from '../utils/config';
import { GetDecryptedString, decodeJWT } from '../utils/encryption'; // 新增導入
import i18n from '../i18n'; // 新增 i18n 導入

const BASE_URL = `${API_BASE_URL}/ctrag`;
const GOOGLE_API_BASE_URL = `${API_BASE_URL}/google`;
const DRIVE_API_BASE_URL = `${GOOGLE_API_BASE_URL}/drive`;

// 快取相關變數
const TOKEN_BUFFER_TIME = 5 * 60 * 1000; // 5分鐘 = 300000毫秒
const TOKEN_VALIDITY_DURATION = 10 * 60 * 1000; // 10分鐘（毫秒）
const STORAGE_KEYS = {
  GOOGLE_TOKEN: 'cached_google_token',
  TOKEN_EXPIRATION: 'google_token_expiration'
};

// 快取相關函數
const getCachedToken = () => {
  try {
    return {
      token: localStorage.getItem(STORAGE_KEYS.GOOGLE_TOKEN),
      expiration: parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRATION))
    };
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.readCachedTokenError'), error);
    return { token: null, expiration: null };
  }
};

const setCachedToken = (token, expiration) => {
  try {
    localStorage.setItem(STORAGE_KEYS.GOOGLE_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, expiration.toString());
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.saveCachedTokenError'), error);
  }
};

export const checkSourcesExist = async (sourceItems) => {
  try {
    const response = await axios.post(`${BASE_URL}/check_sources_exist`, { source_items: sourceItems });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.checkChecksumsError'), error);
    throw error;
  }
};

export const dispatch = async (dispatchPayload) => {
  try {
    const response = await axios.post(`${BASE_URL}/dispatch`, {
      collection: dispatchPayload.collection,
      retrieval: dispatchPayload.retrieval,
      transformation: dispatchPayload.transformation,
      augmentation: dispatchPayload.augmentation,
      generation: dispatchPayload?.generation,
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.dispatchTaskError'), error);
    throw error;
  }
};

// Update the chat function to handle streaming
export const chat = async (chatPayload) => {
  try {
    const response = await fetch(`${BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(chatPayload),
    });

    if (!response.ok) {
      throw new Error(i18n.t('api.ctrag.error.chatRequestFailed'));
    }

    // Return the response object directly for streaming
    return response;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.chatRequestFailed'), error);
    throw error;
  }
};

// WebSocket chat function
export const wsChat = () => {
  const token = GetDecryptedString(window.localStorage.getItem('token'));
  let clearToken = token.replace('Bearer ', '');

  let ws = null;
  let messageCallback = null;
  let errorCallback = null;
  let isConnected = false;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 3;
  let messageQueue = [];
  let connectPromise = null;

  const connect = () => {
    if (connectPromise) return connectPromise;

    connectPromise = new Promise((resolve, reject) => {
      try {
        ws = new WebSocket(`${WS_BASE_URL}?token=${clearToken}`);
        ws.onopen = () => {
          console.debug(i18n.t('api.ctrag.console.websocketConnected'));
          isConnected = true;
          reconnectAttempts = 0;
          if (IS_LOCAL_DEV && WS_BASE_URL.includes('localhost')) {
            // 初始化連線時傳送 token
            ws.send(JSON.stringify({ token }));
          }
          resolve(ws);
          
          // 處理排隊的消息
          while (messageQueue.length > 0) {
            const { message, resolve: queuedResolve } = messageQueue.shift();
            try {
              message.token = clearToken;
              ws.send(JSON.stringify(message));
              queuedResolve();
            } catch (error) {
              console.error('Failed to send queued message:', error);
            }
          }
        };

        ws.onclose = (event) => {
          console.debug(i18n.t('api.ctrag.console.websocketClosed'), event);
          isConnected = false;
          connectPromise = null;
          
          if (event.code !== 1000) { // 非正常關閉才重連
            // 嘗試重新連接
            if (reconnectAttempts < maxReconnectAttempts) {
              reconnectAttempts++;
              setTimeout(() => {
                console.debug(i18n.t('api.ctrag.console.reconnectAttempt', { attempts: reconnectAttempts, max: maxReconnectAttempts }));
                connect();
              }, 1000 * Math.pow(2, reconnectAttempts)); // 指數退避
            }
          }
        };

        ws.onerror = (error) => {
          console.error(i18n.t('api.ctrag.console.websocketError'), error);
          isConnected = false;
          if (errorCallback) {
            errorCallback(error);
          }
          reject(error);
        };

        ws.onmessage = (event) => {
          if (event.data === 'null') return;
          
          try {
            const data = JSON.parse(event.data);
            if (messageCallback) {
              messageCallback(data);
            }
          } catch (error) {
            console.error(i18n.t('api.ctrag.console.websocketErrorParsing'), error);
            if (errorCallback) {
              errorCallback(new Error('Failed to parse message'));
            }
          }
        };

      } catch (error) {
        console.error('WebSocket connection error:', error);
        connectPromise = null;
        reject(error);
      }
    });

    return connectPromise;
  };

  // 初始連接
  connect();

  return {
    // 發送消息
    sendMessage: async (chatPayload) => {
      // 如果沒有連接，先嘗試連接
      if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
        return new Promise((resolve, reject) => {
          messageQueue.push({
            message: chatPayload,
            resolve,
            reject
          });
          
          // 嘗試重新連接
          connect().catch(reject);
        });
      }

      // 如果已經連接，直接發送
      return new Promise((resolve, reject) => {
        try {
          chatPayload.token = clearToken;
          ws.send(JSON.stringify(chatPayload));
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },

    // 設置消息處理回調
    onMessage: (callback) => {
      messageCallback = callback;
    },

    // 設置錯誤處理回調
    onError: (callback) => {
      errorCallback = callback;
    },

    // 關閉連接
    close: () => {
      if (ws) {
        isConnected = false;
        ws.close(1000, "Normal closure"); // 使用正常關閉代碼
      }
    },

    // 檢查連接狀態
    isConnected: () => {
      return isConnected && ws && ws.readyState === WebSocket.OPEN;
    },

    // 重新連接
    reconnect: () => {
      if (ws) {
        ws.close();
      }
      return connect();
    }
  };
};

export const getRelatedQuestions = async (chatSessionId) => {
  try {
    const response = await axios.get(`${BASE_URL}/get_related_questions/${chatSessionId}`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.fetchRelatedQuestionsError'), error);
    throw error;
  }
};

export const getEventDetails = async (eventId, event_progress) => {
  try {
    const url = event_progress 
      ? `${BASE_URL}/event/${eventId}?event_progress=${event_progress}` 
      : `${BASE_URL}/event/${eventId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getEventDetailsError'), error);
    throw error;
  }
};

export const getEventStatus = async (eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/event_status/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getEventStatusError'), error);
    throw error;
  }
};

export const getEventStatusDetail = async (eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/event_status_detail/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getEventStatusDetailError'), error);
    throw error;
  }
};

export const getResult = async (eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/result/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getResultError'), error);
    throw error;
  }
};

export const getResultDetail = async (eventId) => {
  try {
    const response = await axios.get(`${BASE_URL}/result_detail/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getResultDetailError'), error);
    throw error;
  }
};

export const embedding = async (embeddingRequest) => {
  try {
    const response = await axios.post(`${BASE_URL}/embedding`, embeddingRequest);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.embeddingRequestError'), error);
    throw error;
  }
};

// 新增獲取使用者聊天會話列表的函數
export const getChatSessionsByUserEmail = async (page = 1, page_size = 10) => {
  try {
    const response = await axios.get(`${BASE_URL}/chat_session`, {
      params: {
        page: page,        // 新增 page 參數
        pageSize: page_size,   // 新增 page_size 參數
      },
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getChatSessionsError'), error);
    throw error;
  }
};

// 修改 getChatSessionHistoryById 函數，加入 page 和 page_size 參數
export const getChatSessionHistoryById = async (chatSessionId, contextWindowLength, page = 1, page_size = 10, tag_completion_filter) => {
  try {
    const params = {
      context_window_length: contextWindowLength,
      page: page,
      pageSize: page_size
    };
    if (tag_completion_filter) {
      params.tag_completion_filter = tag_completion_filter;
    }
    const response = await axios.get(`${BASE_URL}/chat_session_history/${chatSessionId}`, {
      params: params,
    });
    return response.data.chat_histories;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getChatHistoryError'), error);
    throw error;
  }
};

// 更新聊天會話標籤
// tagSession now only support 'delete'
export const updateChatTagSession = async (chatSessionId, tagSession) => {
  try {
    const response = await axios.post(`${BASE_URL}/chat_tag_session`, {
      chat_session_id: chatSessionId,
      tag_session: tagSession
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.updateChatTagError'), error);
    throw error;
  }
};

// 新增知識庫
export const createLLMKnowledgeBase = async (data) => {
  try {
    const response = await axios.post(`${BASE_URL}/llm_knowledge_base`, data);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.createKnowledgeBaseError'), error);
    throw error;
  }
};

// 獲取知識庫
export const getLLMKnowledgeBase = async (params) => {
  try {
    const response = await axios.get(`${BASE_URL}/llm_knowledge_base`, {
      params: params,
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getKnowledgeBaseError'), error);
    throw error;
  }
};

// 獲取使用者可使用的知識庫
export const getLLMKnowledgeBaseByUser = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/llm_knowledge_base/user`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getUserKnowledgeBaseError'), error);
    throw error;
  }
};

// 獲取使用者管理的知識庫
export const getLLMKnowledgeBaseByManager = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/llm_knowledge_base/manager`);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getManagerKnowledgeBaseError'), error);
    throw error;
  }
};

// 更新或插入知識庫
export const upsertLLMKnowledgeBase = async (data) => {
  try {
    const response = await axios.put(`${BASE_URL}/llm_knowledge_base`, data);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.upsertKnowledgeBaseError'), error);
    throw error;
  }
};

// 刪除知識庫
// @param {string} kbId - Knowledge base ID (kb_xxxxxxxxxxxx)
// @param {string} [knowledgeBaseName] - [DEPRECATED] Use kbId instead
export const removeLLMKnowledgeBase = async (kbId, knowledgeBaseName = null) => {
  try {
    const params = {};
    if (kbId) {
      params.kbId = kbId;
    } else if (knowledgeBaseName) {
      // Legacy support
      console.warn('removeLLMKnowledgeBase: knowledgeBaseName is deprecated, use kbId instead');
      params.knowledgeBaseName = knowledgeBaseName;
    }
    const response = await axios.delete(`${BASE_URL}/llm_knowledge_base`, {
      params: params,
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.removeKnowledgeBaseError'), error);
    throw error;
  }
};

// 更新或插入知識庫的 RAG chunk text
// @param {string} kb_id - Knowledge base ID (kb_xxxxxxxxxxxx)
// @param {string} [knowledge_base_name] - [DEPRECATED] Use kb_id instead
// @param {string} collection - Collection name
// @param {Array<string>} chunk_text - Chunk texts to upsert
export const upsertLLMKnowledgeRAGChunkText = async ({ kb_id, knowledge_base_name, collection, chunk_text }) => {
  try {
    const body = { chunk_text };
    if (kb_id) {
      body.kb_id = kb_id;
    } else if (knowledge_base_name) {
      // Legacy support
      console.warn('upsertLLMKnowledgeRAGChunkText: knowledge_base_name is deprecated, use kb_id instead');
      body.knowledge_base_name = knowledge_base_name;
    }
    if (collection) {
      body.collection = collection;
    }
    const response = await axios.put(
      `${BASE_URL}/llm_knowledge_base/upsert_knowledge_rag_chunk_text`,
      body
    );
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.upsertKnowledgeChunkError'), error);
    throw error;
  }
};

// force update knowledge base
// @param {string} kbId - Knowledge base ID (kb_xxxxxxxxxxxx)
// @param {string} [knowledgeBaseName] - [DEPRECATED] Use kbId instead
export const upsertLLMKnowledgeRAGDrive = async (kbId, knowledgeBaseName = null) => {
  try {
    const body = {};
    if (kbId) {
      body.kb_id = kbId;
    } else if (knowledgeBaseName) {
      // Legacy support
      console.warn('upsertLLMKnowledgeRAGDrive: knowledgeBaseName is deprecated, use kbId instead');
      body.knowledge_base_name = knowledgeBaseName;
    }
    const response = await axios.put(`${BASE_URL}/llm_knowledge_base/upsert_knowledge_rag_drive`, body);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.forceUpdateKnowledgeBaseError'), error);
    throw error;
  }
};

// 獲取 Google Drive 資料夾資訊 for knowledge base
// @param {string} [kb_id] - Knowledge base ID (kb_xxxxxxxxxxxx)
// @param {string} [knowledge_base_name] - [DEPRECATED] Use kb_id instead
export const getDriveFolderInfo = async (params) => {
  try {
    const { drive_folder_id, exclude_google_ids, exclude_type, kb_id, knowledge_base_name, continuation_token } = params;
    const body = {
      drive_folder_id,
      exclude_google_ids,
      exclude_type,
      continuation_token
    };
    if (kb_id) {
      body.kb_id = kb_id;
    } else if (knowledge_base_name) {
      // Legacy support
      console.warn('getDriveFolderInfo: knowledge_base_name is deprecated, use kb_id instead');
      body.knowledge_base_name = knowledge_base_name;
    }
    const response = await axios.post(`${BASE_URL}/drive_folder_info`, body);
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getDriveFolderInfoError'), error);
    throw error;
  }
};

// 修改 getGoogleToken 函數
export const getGoogleToken = async () => {
  try {
    // 檢查是否有有效的快取 token
    const { token, expiration } = getCachedToken();
    const now = Date.now();
    
    // 確保 token 存在且尚未過期（加入緩衝時間）
    if (token && expiration && now < expiration - TOKEN_BUFFER_TIME) {
      return token;
    }

    // Token 不存在或已過期，重新獲取
    const response = await axios.get(`${GOOGLE_API_BASE_URL}/get-google-token`);
    const shortTermToken = response.data.short_term_token;
    const decodedToken = decodeJWT(shortTermToken);
    if (!decodedToken) {
      throw new Error(i18n.t('api.ctrag.error.userCredentialsNotFound'));
    }

    // 更新快取，設定過期時間為當前時間加上有效期限
    const newToken = decodedToken.google_access_token;
    const newExpiration = Date.now() + TOKEN_VALIDITY_DURATION;
    setCachedToken(newToken, newExpiration);

    return newToken;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getGoogleTokenError'), error);
    if (error.response && error.response.status === 404) {
      throw new Error(i18n.t('api.ctrag.error.userCredentialsNotFound'));
    } else {
      throw error;
    }
  }
};

// 獲取多個文件或文件夾
export const getFiles = async (fileIds) => {
  try {
    const response = await axios.post(`${DRIVE_API_BASE_URL}/files`, {
      file_ids: fileIds
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getFilesError'), error);
    throw error;
  }
};

// 獲取多個文件或文件夾的詳細信息
export const getDriveFiles = async (fileIds, popError = true) => {
  try {
    const response = await axios.post(`${BASE_URL}/drive_files`, {
      file_ids: fileIds
    });

    // 檢查每個文件的狀態
    for (const file of response.data) {
      if (file.status === 3) {
        if (popError) {
          throw new Error(i18n.t('api.ctrag.error.fileLimitReached', { fileName: file.file_info.name }));
        }
      } else if (file.status === 2) {
        if (popError) {
          throw new Error(i18n.t('api.ctrag.error.getFileDetailsFailed', { 
            fileName: file?.file_info?.name || file.source_info.file_id,
            message: file.message 
          }));
        }
      }
    }

    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getDriveFilesError'), error);
    if (error.response || error.message) {
      switch (error?.response?.status) {
        case 400:
          throw new Error(i18n.t('api.ctrag.error.noFileIdProvided'));
        case 404:
          throw new Error(i18n.t('api.ctrag.error.fileNotFound'));
        default:
          throw new Error(i18n.t('api.ctrag.error.getFilesFailed', { 
            detail: error?.response?.data?.detail || error?.message || '未知錯誤'
          }));
      }
    } else {
      throw new Error(i18n.t('api.ctrag.error.networkError'));
    }
  }
};

// 更新聊天回應的評分
export const updateChatThumb = async (chatCompletionId, thumbs) => {
  try {
    const response = await axios.post(`${BASE_URL}/thumbs`, {
      chat_completion_id: chatCompletionId,
      thumbs: thumbs
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.updateChatThumbError'), error);
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error(i18n.t('api.ctrag.error.invalidRatingRequest'));
        case 404:
          throw new Error(i18n.t('api.ctrag.error.chatResponseNotFound'));
        default:
          throw new Error(i18n.t('api.ctrag.error.updateRatingFailed', { 
            detail: error.response.data.detail || '未知錯誤'
          }));
      }
    } else {
      throw new Error(i18n.t('api.ctrag.error.networkError'));
    }
  }
};

// 獲取指定知識庫的聊天歷史記錄
export const getChatSessionsByKnowledgeBase = async (knowledgeBaseId, searchText = "", titleSearch = "", page = 1, pageSize = 10) => {
  try {
    if (page < 1) {
      throw new Error(i18n.t('api.ctrag.error.pageNumberInvalid'));
    }
    if (pageSize < 1 || pageSize > 100) {
      throw new Error(i18n.t('api.ctrag.error.pageSizeInvalid'));
    }

    const response = await axios.get(`${BASE_URL}/chat_history_by_knowledge_base_id/${knowledgeBaseId}`, {
      params: {
        page,
        pageSize,
        search_text: searchText,
        title_search: titleSearch
      }
    });

    // 如果沒有聊天歷史記錄，返回空數組和總數為0
    if (!response.data.chat_histories) {
      return {
        chat_histories: [],
        total: 0
      };
    }

    return {
      chat_histories: response.data.chat_histories,
      total: response.data.total
    };
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getChatSessionsByKnowledgeBaseError'), error);
    if (error.response) {
      switch (error.response.status) {
        case 404:
          throw new Error(i18n.t('api.ctrag.error.knowledgeBaseNotFound'));
        case 500:
          throw new Error(i18n.t('api.ctrag.error.getChatHistoryServerError', { 
            detail: error.response.data.detail 
          }));
        default:
          throw new Error(i18n.t('api.ctrag.error.getChatHistoryFailed', { 
            detail: error.response.data.detail || '未知錯誤'
          }));
      }
    } else {
      throw new Error(i18n.t('api.ctrag.error.networkError'));
    }
  }
};

/**
 * 獲取 Google 日曆事件。
 * - 如果提供了 eventId，則返回該特定事件的詳細資訊。
 * - 否則，返回在 start 和 end 時間範圍內的事件列表。
 * @param {object} params - 參數對象
 * @param {string} [params.eventId] - 事件 ID。如果提供，則忽略 start 和 end。
 * @param {string} [params.start] - 開始時間 (RFC3339 格式)。列表視圖時若無 eventId 則需要。
 * @param {string} [params.end] - 結束時間 (RFC3339 格式)。列表視圖時若無 eventId 則需要。
 * @param {string} [params.fields="minimal"] - 字段級別: "minimal", "standard", "extended"。控制返回的字段集。
 * @param {number} [params.attendeesLimit=20] - "extended" 字段級別時的最大參與者數量 (ge=0)。
 * @returns {Promise<object|Array<object>>} 事件對象或事件對象數組。
 * @throws {Error} 如果請求失敗或發生錯誤。
 */
export const getCalendarEvents = async (params = {}) => {
  try {
    const { eventId, start, end, fields, attendeesLimit } = params;

    const queryParams = {};

    if (eventId) {
      queryParams.eventId = eventId;
    } else {
      // 如果沒有 eventId，後端期望 start 和 end 參數用於列表視圖
      if (start) queryParams.start = start;
      if (end) queryParams.end = end;
    }

    if (fields) {
      queryParams.fields = fields;
    }
    if (attendeesLimit !== undefined) {
      queryParams.attendeesLimit = attendeesLimit;
    }

    const response = await axios.get(`${BASE_URL}/calendar/events`, {
      params: queryParams
    });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getCalendarEventsError'), error);
    if (error.response) {
      const detail = error.response.data?.detail || '伺服器回傳了未知的錯誤訊息';
      switch (error.response.status) {
        case 400: // Bad Request - 例如：缺少 start/end，日期格式錯誤
          throw new Error(i18n.t('api.ctrag.error.calendarRequestParamError', { detail }));
        case 404: // Not Found - 例如：事件 ID 未找到
          throw new Error(i18n.t('api.ctrag.error.calendarResourceNotFound', { detail }));
        case 500: // Internal Server Error - 後端內部問題
          throw new Error(i18n.t('api.ctrag.error.calendarServerError', { detail }));
        case 502: // Bad Gateway - 上游 Google API 錯誤
          throw new Error(i18n.t('api.ctrag.error.calendarUpstreamError', { detail }));
        default:
          throw new Error(i18n.t('api.ctrag.error.calendarUnknownError', { detail }));
      }
    } else if (error.request) {
      // 請求已發出，但沒有收到回應
      throw new Error(i18n.t('api.ctrag.error.calendarNetworkFailed'));
    } else {
      // 在設定請求時發生了一些事情，觸發了錯誤
      throw new Error(i18n.t('api.ctrag.error.calendarRequestProcessingError', { message: error.message }));
    }
  }
};

/**
 * Get chunk reference details by ref_code.
 * @param {string} refCode - Reference code in format "doc_id:seq:chunk_type:chunk_size"
 * @param {string|null} collection - Optional collection name
 * @returns {Promise<Object>} Chunk reference response containing doc_id, source_name, chunks, etc.
 */
export const getChunkReference = async (refCode, collection = null) => {
  try {
    const params = { ref_code: refCode };
    if (collection) {
      params.collection = collection;
    }
    const response = await axios.get(`${BASE_URL}/reference`, { params });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getChunkReferenceError'), error);
    if (error.response) {
      const detail = error.response.data?.detail || 'Unknown error';
      switch (error.response.status) {
        case 400:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceInvalidRequest', { detail }));
        case 404:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceNotFound', { detail }));
        default:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceError', { detail }));
      }
    } else {
      throw new Error(i18n.t('api.ctrag.error.networkError'));
    }
  }
};

/**
 * Get only the content of the referenced chunk (lightweight version).
 * @param {string} refCode - Reference code in format "doc_id:seq:chunk_type:chunk_size"
 * @param {string|null} collection - Optional collection name
 * @returns {Promise<string>} The chunk content
 */
export const getChunkReferenceContent = async (refCode, collection = null) => {
  try {
    const params = { ref_code: refCode };
    if (collection) {
      params.collection = collection;
    }
    const response = await axios.get(`${BASE_URL}/reference/content`, { params });
    return response.data;
  } catch (error) {
    console.error(i18n.t('api.ctrag.console.getChunkReferenceContentError'), error);
    if (error.response) {
      const detail = error.response.data?.detail || 'Unknown error';
      switch (error.response.status) {
        case 400:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceInvalidRequest', { detail }));
        case 404:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceNotFound', { detail }));
        default:
          throw new Error(i18n.t('api.ctrag.error.chunkReferenceError', { detail }));
      }
    } else {
      throw new Error(i18n.t('api.ctrag.error.networkError'));
    }
  }
};
